import { Box, useSlotRecipe, HStack } from '@chakra-ui/react';

export type StepperVariant = 'UserControlled' | 'SystemControlled';

export interface StepConfig {
  isActive: boolean | (() => boolean);
  isComplete: boolean | (() => boolean);
}

type BaseStepperProps = Omit<React.ComponentProps<typeof HStack>, 'children'> & {
  variant?: StepperVariant;
};

export type StepperProps = BaseStepperProps &
  (
    | { currentStep?: never; steps: StepConfig[]; totalSteps?: never }
    | { currentStep: number; steps?: never; totalSteps: number }
  );

function resolveValue(value: boolean | (() => boolean)): boolean {
  return typeof value === 'function' ? value() : value;
}

function getStepState(step: StepConfig): 'completed' | 'active' | 'inactive' {
  if (resolveValue(step.isComplete)) return 'completed';
  if (resolveValue(step.isActive)) return 'active';
  return 'inactive';
}

export function Stepper({ variant = 'UserControlled', ref, ...props }: StepperProps) {
  const recipe = useSlotRecipe({ key: 'stepper' });
  const styles = recipe({ variant });

  let stepStates: ('completed' | 'active' | 'inactive')[];
  let restProps: Record<string, unknown>;

  if ('steps' in props && props.steps) {
    const { steps, ...rest } = props;
    stepStates = steps.map(getStepState);
    restProps = rest;
  } else {
    const { currentStep, totalSteps, ...rest } = props;
    stepStates = Array.from({ length: totalSteps }, (_, idx) => {
      const step = idx + 1;
      if (step < currentStep) return 'completed';
      if (step === currentStep) return 'active';
      return 'inactive';
    });
    restProps = rest;
  }

  return (
    <HStack ref={ref} css={styles.root['@layer recipes']} {...restProps}>
      {stepStates.map((state, idx) => (
        <Box key={idx} css={styles.step['@layer recipes']} data-state={state} />
      ))}
    </HStack>
  );
}
